import produce from 'immer';

import { FormStateValues } from '@components/form';
import { parseDate } from '@utils/date';

import { isOptionsField, selectedListOptions } from './list-options';
import { FieldType, FormListsMap, FormV2 } from './types';

export const prepareFormDataV2 = (
  form: FormV2['fields'],
  values: FormStateValues,
  lists: FormListsMap = {}
) => {
  const merged = mergeFormValues(form, values);
  return formToFormDataV2(merged, lists);
};

export const mergeFormValues = <T extends FieldType>(form: T[], state: FormStateValues) => {
  return produce(form, (draft) => {
    return draft.forEach((field) => {
      if ('fields' in field) {
        field.fields = mergeFormValues(field.fields, state);
      }
      if (field.id in state) {
        if (field.kind === 'input-file') {
          if (!field.allowMultiple) {
            const stateValue = state[field.id];
            if (isRealFile(stateValue)) {
              // @ts-ignore
              field.value = stateValue;
            }
            if (!stateValue) {
              field.value = [];
            }
          } else {
            const stateValue = state[field.id];
            if (Array.isArray(stateValue)) {
              const processedValue = stateValue.map((item) => {
                if (isRealFile(item)) {
                  return item;
                } else if (item && typeof item === 'object' && 'attachmentId' in item) {
                  return item.attachmentId;
                } else if (typeof item === 'number') {
                  return item;
                }
                return item;
              });
              field.value = processedValue;
            }
          }
        } else {
          // value doesn't exist on all fields so it will error
          // but we know if the state contains this field, it has to be a value field
          // @ts-ignore
          field.value = state[field.id];
        }
      } else if ('value' in field && field.value) {
        // Field is not in state (e.g., conditionally hidden) - clear its value
        // to prevent previously saved values from being submitted
        // @ts-ignore
        field.value = undefined;
      }
    });
  });
};

export const isRealFile = (v: any): v is File => {
  try {
    return typeof File !== 'undefined' && v instanceof File;
  } catch {
    return false;
  }
};

export const formToFormDataV2 = <T extends FieldType>(
  form: T[],
  lists: FormListsMap = {}
): FormData => {
  let files: Record<string, File[]> = {};

  // Keep only the chosen option(s) on list-backed fields so the payload carries
  // one option, not the whole list (a large list would trip the request limit).
  const formWithSelectedOptions = snapshotListBackedOptions(form, lists);
  const extractedForm = extractFilesFromForm(formWithSelectedOptions, files);

  const formData = new FormData();
  formData.append('fieldsConfig', JSON.stringify({ schema: 'v2', fields: extractedForm }));

  Object.keys(files).forEach((id) => {
    files[id].forEach((file, index) => {
      if (isRealFile(file)) {
        formData.append(`${id}__${index}`, file);
      }
    });
  });

  return formData;
};

const snapshotListBackedOptions = <T extends FieldType>(form: T[], lists: FormListsMap) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if ('fields' in field) {
        field.fields = snapshotListBackedOptions(field.fields, lists);
      }
      if (isOptionsField(field)) {
        field.options = selectedListOptions(field, lists);
      }
    });
  });
};

const extractFilesFromForm = <T extends FieldType>(form: T[], files: Record<string, File[]>) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (field.kind === 'input-file' && field.value) {
        if (!field.allowMultiple) {
          if (isRealFile(field.value)) {
            files[field.id] = (files[field.id] ?? []).concat(field.value);
          }
        } else {
          const fileObjects: File[] = [];
          const attachmentIds: number[] = [];
          const values = Array.isArray(field.value) ? field.value : [field.value];

          values.forEach((item) => {
            if (isRealFile(item)) {
              fileObjects.push(item);
            } else if (typeof item === 'number') {
              attachmentIds.push(item);
            }
          });
          if (fileObjects.length > 0) {
            files[field.id] = (files[field.id] ?? []).concat(fileObjects);
          }
          field.value = attachmentIds;
        }
      }
      if (field.kind === 'input-date' && field.value) {
        field.value = formatDateWithoutTimezone(field.value);
      }

      if ('fields' in field) {
        field.fields = extractFilesFromForm(field.fields, files);
      }
    });
  });
};

export const formatDateWithoutTimezone = (date: string | Date) => {
  if (!date) {
    return '';
  }

  const localDate = parseDate(date);
  const formatPart = (part: number) => String(part).padStart(2, '0');

  const year = localDate.getFullYear();
  const month = formatPart(localDate.getMonth() + 1);
  const day = formatPart(localDate.getDate());

  return `${year}-${month}-${day}T00:00:00Z`;
};
